import * as moment from "moment";

import { formatMoney } from "../../../Shared/React/constants/moneyFormat";
import {
  IColumnDefinition,
  IInvoiceTableProperties,
} from "../../../Shared/React/typings/api-models/invoice/InvoiceTable";
import InvoiceApiRoutes from "../../../Shared/React/typings/api-routes/InvoiceApiRoutes";

import React = require("react");
export enum InvoiceGroup {
  Residential = "Residential",
  Community = "Community",
  Pending = "Pending",
  Commercial = "Commercial",
}

export enum InvoiceType {
  Standard = "Standard",
  Duplicate = "Duplicate",
  Mismatch = "Mismatch",
  Multiple = "Multiple",
  SingleSelect = "Single",
  StandardSent = "Sent",
}

enum ColumnType {
  Money,
  Kwh,
  Date,
  Note,
}

export function getTableSetup(
  tableName: string,
  invoiceType: InvoiceType,
  invoiceGroup: InvoiceGroup,
  accessor?: string,
  method?: any,
  approveMethod?: any,
  alternateApproveMethod?: any,
  previewDisabledMethod?: any,
): IInvoiceTableProperties {
  return {
    tableName,
    tableId: getTableId(invoiceType, invoiceGroup),
    title: getTitle(invoiceType),
    invoiceGroup: invoiceGroup,
    ignoreText: getIgnoreText(invoiceGroup),
    columns: buildColumns(
      invoiceType,
      invoiceGroup,
      accessor,
      method,
      approveMethod,
      alternateApproveMethod,
      tableName,
      previewDisabledMethod,
    ),
    subComponentColumns: buildSubComponentColumns(invoiceGroup, invoiceType),
    showPagination: shouldPaginate(invoiceType, invoiceGroup),
    showApproveButton: showApprove(invoiceType, invoiceGroup),
    showApproveAllButton: showApproveAll(invoiceType, invoiceGroup),
    dataUrl: getDataUrl(invoiceType, invoiceGroup),
    filterable: shouldFilter(invoiceType, invoiceGroup),
    filterBy: "note",
    pivotBy: [accessor],
    keyField: getKeyField(invoiceGroup),
  };
}

function getTableId(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): number {
  switch (invoiceGroup) {
    case InvoiceGroup.Residential:
      switch (invoiceType) {
        case InvoiceType.Standard:
          return 0;
        case InvoiceType.Duplicate:
          return 1;
        case InvoiceType.Mismatch:
          return 2;
      }
      break;
    case InvoiceGroup.Community:
      switch (invoiceType) {
        case InvoiceType.Standard:
          return 3;
        case InvoiceType.Duplicate:
          return 4;
        case InvoiceType.Mismatch:
          return 5;
      }
    case InvoiceGroup.Pending:
      switch (invoiceType) {
        case InvoiceType.Standard:
          return 6;
        case InvoiceType.Duplicate:
          return 7;
        case InvoiceType.Mismatch:
          return 8;
      }
    case InvoiceGroup.Commercial:
      switch (invoiceType) {
        case InvoiceType.Standard:
          return 9;
        case InvoiceType.Multiple:
          return 10;
        case InvoiceType.SingleSelect:
          return 11;
        case InvoiceType.StandardSent:
          return 12;
      }
    default:
      return null;
  }
}

function getTitle(invoiceType: InvoiceType): string {
  switch (invoiceType) {
    case InvoiceType.Duplicate:
      return "Duplicates";
    case InvoiceType.Mismatch:
      return "Mismatches";
    case InvoiceType.SingleSelect:
      return "Eversource";
    default:
      return invoiceType;
  }
}

function getIgnoreText(invoiceGroup: InvoiceGroup): string {
  if (invoiceGroup === InvoiceGroup.Pending) {
    return "Remove Selected";
  }
  return "Ignore Selected";
}

function buildColumns(
  invoiceType: InvoiceType,
  invoiceGroup: InvoiceGroup,
  accessor: string,
  method: any,
  approveMethod: any,
  alternateApproveMethod: any,
  tableName: string,
  previewDisabledMethod?: any,
): IColumnDefinition[] {
  let columns: any[] = [
    {
      expander: shouldHaveExpander(invoiceType, invoiceGroup),
      sortable: false,
      show: shouldShow(invoiceGroup, invoiceType),
      resizable: false,
    },
  ];
  if (invoiceGroup === InvoiceGroup.Pending) {
    columns = columns.concat(getPendingColumns(invoiceType));
  } else if (invoiceGroup === InvoiceGroup.Commercial) {
    columns = columns.concat(
      getCommercialColumns(
        invoiceType,
        invoiceGroup,
        method,
        approveMethod,
        alternateApproveMethod,
        tableName,
        previewDisabledMethod,
      ),
    );
  } else {
    if (accessor) {
      columns.push({ accessor: accessor });
    }

    columns.push(getCustomerNumberColumn(invoiceType, invoiceGroup), getStandardColumn("Due Date", "dueDate"));

    if (invoiceGroup === InvoiceGroup.Residential) {
      columns.push(
        getStandardColumn("From Date", "fromDate", 80),
        getStandardColumn("To Date", "toDate", 80),
        getStandardColumn("Contract Type", "productType", 80),
      );
    }

    columns.push(getStandardColumn("Balance", "balanceDue", 100, ColumnType.Money));

    if (invoiceGroup === InvoiceGroup.Community) {
      columns.push(getStandardColumn("Community", "community"));
    }

    if (shouldFilter(invoiceType, invoiceGroup)) {
      columns.push(getStandardColumn("Hold Reason", "note", 100, ColumnType.Note));
    }

    columns.push(getPreviewColumn(invoiceGroup, method, previewDisabledMethod));
  }

  return columns;
}

function getPreviewColumn(invoiceGroup: InvoiceGroup, method: any, previewDisabledMethod?: any): IColumnDefinition {
  let accessor = invoiceGroup !== InvoiceGroup.Community ? "documentKey" : "invoiceNumber";
  return {
    Header: "Preview",
    accessor: accessor,
    className: "center",
    Cell: (row) => {
      if (row.value) {
        const rowMethod = row.value;

        if (invoiceGroup === InvoiceGroup.Commercial || invoiceGroup === InvoiceGroup.Residential) {
          return (
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => method(rowMethod)}
              >
                <i className="fa fa-eye" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => previewDisabledMethod(rowMethod)}
              >
                <i className="fa fa-download" aria-hidden="true"></i>
              </button>
            </div>
          );
        } else {
          return (
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e: React.MouseEvent<HTMLButtonElement>): void => method(rowMethod)}
            >
              {" "}
              Preview{" "}
            </button>
          );
        }
      }
      return null;
    },
    sortable: false,
    resizable: false,
  };
}

function getLongestCommonString(array: Array<string>, property: string): string {
  var A = array
      .map((x) => x[property])
      .concat()
      .sort(),
    a1 = A[0],
    a2 = A[A.length - 1],
    L = a1.length,
    i = 0;
  while (i < L && a1.charAt(i) === a2.charAt(i)) i++;
  var commonName = a1.substring(0, i).trim();
  return commonName.charAt(commonName.length - 1) === "-" ? commonName.slice(0, -1) : commonName;
}

function getCommercialColumns(
  invoiceType: InvoiceType,
  invoiceGroup: InvoiceGroup,
  method: any,
  approveMethod: any,
  alternateApproveMethod: any,
  tableName: string,
  previewDisabledMethod?: any,
): IColumnDefinition[] {
  var columns = [];
  if (
    invoiceType === InvoiceType.Standard ||
    invoiceType === InvoiceType.SingleSelect ||
    invoiceType === InvoiceType.StandardSent
  ) {
    columns = commercialIndividualAccountColumns(invoiceType);
  } else {
    columns = [
      {
        Header: "Name",
        accessor: "stagedInvoices",
        Cell: (row) => {
          var name = "";
          if (row.value.length === 1) {
            name = row.value[0].accountName;
          } else {
            name = getLongestCommonString(row.value, "accountName");
          }
          return <span>{name}</span>;
        },
        resizable: false,
      },
      getStandardColumn("Note", "note", 100, ColumnType.Note),
    ];
  }
  columns.push(getPreviewColumn(invoiceGroup, method, previewDisabledMethod));
  if (invoiceType === InvoiceType.SingleSelect || invoiceType === InvoiceType.Multiple) {
    columns.push({
      Header: "Approve",
      accessor: "commercialInvoiceGroupingId",
      className: "center",
      Cell: (row) => {
        return (
          <button
            type="submit"
            className="btn cyan-btn"
            onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
              row.original && row.original.uploadForApproval
                ? approveMethod(row.value, tableName)
                : alternateApproveMethod(row.value, tableName);
            }}
          >
            {" "}
            Approve{" "}
          </button>
        );
      },
      sortable: false,
      resizable: false,
    });
  }
  return columns;
}

function getPendingColumns(invoiceType: InvoiceType): IColumnDefinition[] {
  var columns = [];
  columns.push(
    getStandardColumn("Name", "fullName"),
    getStandardColumn("Invoice Number", "invoiceNumber"),
    getStandardColumn("Abp Number", "abpAccountNumber"),
    getStandardColumn("Ldc Number", "ldcAccountNumber"),
    getStandardColumn("EPC Partner/Community", "partnerName"),
    getStandardColumn("Fund", "lineOfBusiness"),
    getStandardColumn("Due Date", "dueDate", 100, ColumnType.Date),
  );
  if (invoiceType === InvoiceType.Duplicate) {
    columns.push(getStandardColumn("Invoice Balance", "balanceDueOnInvoice", 100, ColumnType.Money));
  }
  columns.push(getStandardColumn("Current Balance", "currentAmountDue", 100, ColumnType.Money));
  return columns;
}

function buildSubComponentColumns(invoiceGroup: InvoiceGroup, invoiceType: InvoiceType): IColumnDefinition[] {
  if (invoiceGroup === InvoiceGroup.Residential) {
    return [
      getStandardColumn("Quantity", "usageAmount", 100, ColumnType.Kwh),
      getStandardColumn("Gen Charges", "generationCharge", 100, ColumnType.Money),
      getStandardColumn("Other Charges", "otherCharges", 100, ColumnType.Money),
    ];
  } else if (invoiceGroup === InvoiceGroup.Community) {
    return [
      getStandardColumn("Other Charges", "otherCharges", 100, ColumnType.Money),
      getStandardColumn("Transfer", "transferAmount", 100, ColumnType.Money),
      getStandardColumn("Savings", "solarSavings", 100, ColumnType.Money),
    ];
  } else if (invoiceGroup === InvoiceGroup.Commercial) {
    if (
      invoiceType !== InvoiceType.Standard &&
      invoiceType !== InvoiceType.SingleSelect &&
      invoiceType !== InvoiceType.StandardSent
    ) {
      return commercialIndividualAccountColumns(invoiceType);
    }
  }

  return [];
}

function commercialIndividualAccountColumns(invoiceType: InvoiceType) {
  let noSubComponent =
    invoiceType !== InvoiceType.Standard &&
    invoiceType !== InvoiceType.SingleSelect &&
    invoiceType !== InvoiceType.StandardSent;
  return [
    getStandardColumn("Account #", noSubComponent ? "ldcAccountNumber" : "stagedInvoices[0].ldcAccountNumber"),
    getStandardColumn("Name", noSubComponent ? "accountName" : "stagedInvoices[0].accountName", 200),
    getStandardColumn("Charge", noSubComponent ? "charge" : "stagedInvoices[0].charge", 100, ColumnType.Money),
    getStandardColumn("Taxes", noSubComponent ? "taxes" : "stagedInvoices[0].taxes", 100, ColumnType.Money),
    getStandardColumn("From Date", noSubComponent ? "fromDate" : "stagedInvoices[0].fromDate", 100, ColumnType.Date),
    getStandardColumn("To Date", noSubComponent ? "toDate" : "stagedInvoices[0].toDate", 100, ColumnType.Date),
    getStandardColumn(
      "Production",
      noSubComponent ? "productionTotal" : "stagedInvoices[0].productionTotal",
      100,
      ColumnType.Kwh,
    ),
    getStandardColumn("Note", "note", 200, ColumnType.Note),
  ];
}

function shouldShow(invoiceGroup: InvoiceGroup, invoiceType: InvoiceType): boolean {
  return (
    invoiceGroup !== InvoiceGroup.Pending &&
    !(
      invoiceGroup === InvoiceGroup.Commercial &&
      (invoiceType === InvoiceType.Standard ||
        invoiceType === InvoiceType.SingleSelect ||
        invoiceType === InvoiceType.StandardSent)
    )
  );
}

function getStandardColumn(
  header: string,
  accessor: string,
  minWidth?: number,
  columnType?: ColumnType,
): IColumnDefinition {
  var column: IColumnDefinition = {
    Header: header,
    accessor: accessor,
    Cell: (row) => {
      var value =
        columnType === ColumnType.Money
          ? formatMoney(row.value)
          : columnType === ColumnType.Kwh
          ? row.value + " kWh"
          : columnType === ColumnType.Date
          ? moment(row.value).format("L")
          : row.value;
      var title = columnType === ColumnType.Note ? value : "";
      return <span title={title}>{value}</span>;
    },
    resizable: false,
    minWidth: minWidth ? minWidth : 100,
  };

  return column;
}

function shouldHaveExpander(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): boolean {
  //if (invoiceGroup === InvoiceGroup.Community && invoiceType === InvoiceType.Standard) {
  //    return false;
  //}
  return true;
}

function shouldPaginate(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): boolean {
  if (
    invoiceType === InvoiceType.Standard ||
    invoiceGroup === InvoiceGroup.Commercial ||
    invoiceGroup == InvoiceGroup.Pending
  ) {
    return true;
  }
  return false;
}

function getDataUrl(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): string {
  if (invoiceGroup === InvoiceGroup.Commercial) {
    return invoiceType === InvoiceType.StandardSent
      ? InvoiceApiRoutes.getCommercialSentInvoices(invoiceType)
      : InvoiceApiRoutes.getCommercialStagedInvoices(invoiceType);
  }

  if (invoiceGroup === InvoiceGroup.Pending) {
    return InvoiceApiRoutes.getPendingCharges("all", invoiceType);
  }

  return InvoiceApiRoutes.getInvoices(invoiceGroup, invoiceType);
}

function shouldFilter(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): boolean {
  if (
    invoiceGroup !== InvoiceGroup.Pending &&
    (invoiceType === InvoiceType.Duplicate ||
      invoiceType === InvoiceType.Mismatch ||
      invoiceGroup === InvoiceGroup.Commercial)
  ) {
    return true;
  }
  return false;
}

function showApproveAll(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): boolean {
  if (
    invoiceType === InvoiceType.Standard &&
    invoiceGroup !== InvoiceGroup.Pending &&
    invoiceGroup !== InvoiceGroup.Commercial
  ) {
    return true;
  }
  return false;
}

function showApprove(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): boolean {
  if (
    invoiceGroup === InvoiceGroup.Pending ||
    (invoiceGroup === InvoiceGroup.Commercial &&
      (invoiceType === InvoiceType.SingleSelect ||
        invoiceType === InvoiceType.StandardSent ||
        invoiceType === InvoiceType.Multiple))
  ) {
    return false;
  }
  return true;
}

function getCustomerNumberColumn(invoiceType: InvoiceType, invoiceGroup: InvoiceGroup): IColumnDefinition {
  if (invoiceGroup === InvoiceGroup.Community) {
    return getStandardColumn("LDC Number", "excelergyLdcAccountNumber");
  }
  if (invoiceType === InvoiceType.Standard || invoiceType === InvoiceType.Mismatch) {
    return {
      Header: "Ldc Account Number",
      accessor: "excelergyLdcAccountNumber",
      Cell: (row) => getCustomerNumberLink(row.original.igsCustomerNumber, row.value),
      resizable: false,
    };
  } else if (invoiceType === InvoiceType.Duplicate) {
    return {
      Header: "Ldc Account Number",
      id: "link",
      accessor: (d) => d.excelergyLdcAccountNumber,
      Cell: (row) => {
        var customerNumber = row.subRows
          ? row.subRows[0]._original.igsCustomerNumber
          : row.original.igsCustomerNumber;

        return getCustomerNumberLink(customerNumber, row.value);
      },
      resizable: false,
    };
  }
}

function getCustomerNumberLink(customerNumber: string, value: string): any {
  return (
    <a href={"app/#/v2/system-details/" + customerNumber + "/overview"} target="_blank">
      {" "}
      {value}{" "}
    </a>
  );
}

function getKeyField(invoiceGroup: InvoiceGroup): string {
  return invoiceGroup === InvoiceGroup.Commercial ? "commercialInvoiceGroupingId" : "invoiceNumber";
}
